'use client';

import { TextSelection } from '@tiptap/pm/state';
import Image from 'next/image';
import { Button } from '@/components/era-ui/Button';
import { useTipTapEditor } from '@/hooks/annotations/useTipTapEditor';
import { cn } from '@/libs/tailwind';

const Divider = () => (
  <div className="w-[1px] h-5 mx-1 rounded-[1px] bg-[#D4D6EC] self-center" />
);

type ToolbarAction = {
  icon: JSX.Element;
  label: string;
  onClick: () => void;
  active: boolean;
  disabled?: boolean;
};

type ToolbarDivider = {
  type: 'divider';
};

type ToolbarItem = ToolbarAction | ToolbarDivider;

const isToolbarAction = (item: ToolbarItem): item is ToolbarAction => {
  return 'icon' in item;
};

export const TipTapActionBar = () => {
  const { editor } = useTipTapEditor();

  if (!editor) {
    return null;
  }

  const withSelection = (command: () => void) => {
    const { state } = editor;
    const { from, to } = state.selection;
    editor.commands.setTextSelection({ from, to });
    command();
  };

  const toolbarActions: ToolbarItem[] = [
    {
      icon: <Image src="/assets/icons/undo-icon.svg" alt="Undo" width={12} height={12} />,
      label: 'Undo',
      onClick: () => withSelection(() => editor.chain().undo().run()),
      active: false,
      disabled: !editor.can().undo(),
    },
    {
      icon: <Image src="/assets/icons/redo-icon.svg" alt="Redo" width={12} height={12} />,
      label: 'Redo',
      onClick: () => withSelection(() => editor.chain().redo().run()),
      active: false,
      disabled: !editor.can().redo(),
    },
    { type: 'divider' },
    {
      icon: <Image src="/assets/icons/h1-icon.svg" alt="Heading 1" width={16} height={16} />,
      label: 'Heading 1',
      onClick: () => withSelection(() => editor.commands.toggleHeading({ level: 1 })),
      active: editor.isActive('heading', { level: 1 }),
    },
    {
      icon: <Image src="/assets/icons/h2-icon.svg" alt="Heading 2" width={16} height={16} />,
      label: 'Heading 2',
      onClick: () => withSelection(() => editor.commands.toggleHeading({ level: 2 })),
      active: editor.isActive('heading', { level: 2 }),
    },
    {
      icon: <Image src="/assets/icons/h3-icon.svg" alt="Heading 3" width={16} height={16} />,
      label: 'Heading 3',
      onClick: () => withSelection(() => editor.commands.toggleHeading({ level: 3 })),
      active: editor.isActive('heading', { level: 3 }),
    },
    { type: 'divider' },
    {
      icon: <Image src="/assets/icons/bold-icon.svg" alt="Bold" width={16} height={16} />,
      label: 'Bold',
      onClick: () => withSelection(() => editor.commands.toggleBold()),
      active: editor.isActive('bold'),
    },
    {
      icon: <Image src="/assets/icons/italic-icon.svg" alt="Italic" width={16} height={16} />,
      label: 'Italic',
      onClick: () => withSelection(() => editor.commands.toggleItalic()),
      active: editor.isActive('italic'),
    },
    { type: 'divider' },
    {
      icon: <Image src="/assets/icons/latex-icon.svg" alt="Insert LaTeX" width={16} height={16} />,
      label: 'Insert LaTeX',
      onClick: () => withSelection(() => {
        // This function handles LaTeX insertion in the editor:
        // 1. Gets the current editor state and view
        // 2. Inserts '$ $' at the current cursor position
        // 3. Moves the cursor between the dollar signs
        // 4. Focuses the editor for immediate typing
        const { state, view } = editor;
        const { from } = state.selection;

        editor.commands.command(({ tr, dispatch }) => {
          if (!dispatch) {
            return false;
          }

          tr.insertText('$ $', from);
          tr.setSelection(TextSelection.create(tr.doc, from + 1));
          dispatch(tr);
          view.focus();
          return true;
        });
      }),
      active: editor.isActive('mathematics'),
    },
    {
      icon: <Image src="/assets/icons/codeblock-icon.svg" alt="Code Block" width={16} height={16} />,
      label: 'Code Block',
      onClick: () => withSelection(() => editor.commands.toggleCodeBlock()),
      active: editor.isActive('codeBlock'),
    },
    {
      icon: <Image src="/assets/icons/numbered-list-icon.svg" alt="Numbered List" width={16} height={16} />,
      label: 'Numbered List',
      onClick: () => withSelection(() => editor.commands.toggleOrderedList()),
      active: editor.isActive('orderedList'),
    },
    {
      icon: <Image src="/assets/icons/bullet-list-icon.svg" alt="Bullet List" width={16} height={16} />,
      label: 'Bullet List',
      onClick: () => withSelection(() => editor.commands.toggleBulletList()),
      active: editor.isActive('bulletList'),
    },
  ];

  return (
    <div className="-mx-5 -mt-5 inline-flex w-auto px-3 py-2 overflow-hidden rounded-t-[16px] border-b border-b-[0.5px] border-fg-border">
      <div className="flex flex-nowrap gap-1 items-center">
        {toolbarActions.map((action, index) => {
          if (!isToolbarAction(action)) {
            return <Divider key={`divider-${index}`} />;
          }

          return (
            <Button
              key={action.label}
              variant="ghost"
              size="icon"
              onClick={action.onClick}
              disabled={action.disabled}
              active={action.active}
              className={cn(
                'w-8 h-8 p-0',
                action.active && 'bg-accent',
                action.disabled && 'opacity-50 cursor-not-allowed',
              )}
              aria-label={action.label}
              title={action.label}
            >
              {action.icon}
            </Button>
          );
        })}
      </div>
    </div>
  );
};
